import { useState } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bell, Plus, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePolicies, useClaims, useRunPolicyAlerts } from "@/features/insurance/api";
import { POLICY_TYPES, daysToExpiry, formatINR, policyStatus } from "@/features/insurance/types";
import { ExpiryBadge, PolicyStatusBadge } from "@/features/insurance/badges";
import { InsuranceKpis, type InsuranceKpiKey } from "@/features/insurance/InsuranceKpis";
import { UninsuredAlertsCard } from "@/features/insurance/UninsuredAlertsCard";
import { useBranch } from "@/context/BranchContext";
import { useConfigOptions } from "@/features/configuration/api";
import { ListExportButtons } from "@/features/reports/ListExportButtons";
import { canExportInsuranceReports, isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/insurance/")({
  component: InsurancePage,
  validateSearch: (s: Record<string, unknown>) => ({ expiry: s.expiry as string | undefined }),
});

const VIEWER = new Set(["super_admin", "biomed_head", "finance", "management", "facility_manager"]);
const EDITOR = new Set(["super_admin", "biomed_head", "finance"]);

function InsurancePage() {
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const navigate = useNavigate();
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const { expiry } = Route.useSearch();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(expiry === "soon" ? "expiring" : "all");

  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;

  const { data: policies = [], isLoading } = usePolicies({
    type: type === "all" ? null : type,
    search,
  }, effectiveBranchId);
  const { data: claims = [] } = useClaims({}, effectiveBranchId);
  const runAlerts = useRunPolicyAlerts();

  const { data: typeCfg } = useConfigOptions("insurance", "policy_type");
  const typeOptions = (typeCfg ?? []).filter((o) => o.is_active).map((o) => o.value ?? o.label);
  const typeChoices: string[] = typeOptions.length > 0 ? typeOptions : (POLICY_TYPES as readonly string[] as string[]);

  const getTypeLabel = (val: string) => {
    const opt = (typeCfg ?? []).find(o => (o.value || o.label) === val);
    if (opt && opt.label) return opt.label;
    return val.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (!me) return null;
  const roles = me.roles ?? [];
  const canView = roles.some((r) => VIEWER.has(r));
  const canEdit = roles.some((r) => EDITOR.has(r));
  if (!canView) {
    return <Card className="mx-auto max-w-xl"><CardContent className="space-y-2 p-6">
      <h2 className="text-lg font-semibold">Permission required</h2>
      <p className="text-sm text-muted-foreground">You do not have access to the Insurance Register.</p>
    </CardContent></Card>;
  }

  const today = new Date(); today.setHours(0, 0, 0, 0);
  let visible = policies;
  if (status !== "all") visible = visible.filter((p) => policyStatus(p) === status);
  if (tab === "expiring") visible = visible.filter((p) => policyStatus(p) === "Active" && daysToExpiry(p.end_date) <= 30);
  if (tab === "expired") visible = visible.filter((p) => policyStatus(p) === "Expired");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Insurance Register</h1> <ReadOnlyPill module="insurance" />
          <p className="text-sm text-muted-foreground">Asset and property insurance, endorsements, and claims.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ListExportButtons
            rows={() =>
              visible.map((p) => ({
                "Policy No": p.policy_no,
                Type: getTypeLabel(p.type),
                Insurer: p.insurer_name,
                Scope: p.coverage_scope ?? "",
                "Covered Assets": p.asset_count ?? 0,
                "Sum Insured": Number(p.sum_insured ?? 0),
                Premium: Number(p.premium ?? 0),
                "Start Date": p.start_date,
                "End Date": p.end_date,
                Status: policyStatus(p),
                "Days To Expiry": daysToExpiry(p.end_date),
              }))
            }
            filename="Insurance_Register"
            sheetName="Insurance"
            disabled={isLoading || visible.length === 0}
            canExport={canExportInsuranceReports(roles)}
          />
          {canEdit && (
            <Button variant="outline" disabled={runAlerts.isPending} onClick={async () => {
              try { const n = await runAlerts.mutateAsync(); toast.success(`Alert check complete · ${n} notification(s)`); }
              catch (e) { toast.error((e as Error).message); }
            }}>
              <Bell className="mr-1 h-4 w-4" />Run Renewal Alerts
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => navigate({ to: "/insurance/new" })}><Plus className="mr-1 h-4 w-4" />New Policy</Button>
          )}
        </div>
      </div>

      <InsuranceKpis
        policies={policies}
        claims={claims}
        activeKey={
          tab === "expiring" ? "expiring"
            : status === "Active" ? "active"
            : null
        }
        onSelect={(k) => {
          if (!k) { setTab("all"); setStatus("all"); return; }
          if (k === "active") { setTab("all"); setStatus("Active"); return; }
          if (k === "expiring") { setTab("expiring"); setStatus("all"); return; }
          if (k === "open_claims") { /* open claims tab not present; ignore */ return; }
        }}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">All Policies</TabsTrigger>
          <TabsTrigger value="expiring">Expiring (30d)</TabsTrigger>
          <TabsTrigger value="expired">Expired</TabsTrigger>
          <TabsTrigger value="alerts">Uninsured / Underinsured</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts" className="mt-3">
          <UninsuredAlertsCard />
        </TabsContent>

        <TabsContent value={tab === "alerts" ? "" : tab} className="mt-3 space-y-3">
          <Card><CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input className="w-56" placeholder="Search policy/insurer…" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-44"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {typeChoices.map((t) => <SelectItem key={t} value={t}>{getTypeLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent></Card>

          <Card><CardContent className="p-0">
            {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
            visible.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No policies found.</div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Policy No</TableHead><TableHead>Type</TableHead><TableHead>Insurer</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Covered</TableHead><TableHead>Sum Insured</TableHead><TableHead>Premium</TableHead>
                  <TableHead>Valid Till</TableHead><TableHead>Status</TableHead><TableHead>Expiry</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {visible.map((p) => {
                    const d = daysToExpiry(p.end_date);
                    const s = policyStatus(p);
                    return (
                      <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate({ to: "/insurance/$policyId", params: { policyId: p.id } })}>
                        <TableCell className="font-mono text-xs">
                          <Link to="/insurance/$policyId" params={{ policyId: p.id }} className="text-primary hover:underline">{p.policy_no}</Link>
                        </TableCell>
                        <TableCell className="text-sm">{getTypeLabel(p.type)}</TableCell>
                        <TableCell className="text-sm">{p.insurer_name}</TableCell>
                        <TableCell className="text-xs">
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge variant="secondary">{p.coverage_scope ?? "—"}</Badge>
                            {(p.coverage_mode === "Property" || p.coverage_scope === "Building") && (
                              <Badge variant="outline">Property</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">{p.asset_count ?? 0}</TableCell>
                        <TableCell className="text-sm tabular-nums">{formatINR(p.sum_insured)}</TableCell>
                        <TableCell className="text-sm tabular-nums">{formatINR(p.premium)}</TableCell>
                        <TableCell className="text-sm">{p.end_date}</TableCell>
                        <TableCell><PolicyStatusBadge status={s} /></TableCell>
                        <TableCell><ExpiryBadge days={d} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
